import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User, TrendingUp, Heart, Users, MapPin, Sparkles, Calendar, Share2, Download, Info, AlertCircle, ChevronRight } from 'lucide-react';
import { LifeContext, AIProfile, Screen } from '../types';
import SwipeableFeedbackCard from '../components/SwipeableFeedbackCard';

interface MyProfileViewProps {
  context: LifeContext;
  onNavigate: (screen: Screen) => void;
  onContextUpdate?: (updates: Partial<LifeContext>) => void;
}

const STRINGS = {
  Italian: {
    title: 'Profilo',
    subtitle: 'Come Egovex ti vede',
    summary: 'Chi sei',
    values: 'Valori Fondamentali',
    goals: 'Obiettivi di Vita',
    blockers: 'Sfide da Superare',
    people: 'Persone Importanti',
    places: 'Luoghi Significativi',
    traits: 'Tratti Distintivi',
    evolution: 'Evoluzione del Profilo',
    evolutionComment: 'Il tuo profilo si evolve automaticamente in base ai momenti che condividi. Ogni riflessione arricchisce la comprensione di chi sei.',
    version: 'Versione',
    completeness: 'Completezza',
    lastUpdated: 'Ultimo aggiornamento',
    created: 'Creato',
    noProfile: 'Profilo in costruzione...',
    noProfileDesc: 'Completa più momenti per permettermi di conoscerti meglio',
    share: 'Condividi',
    download: 'Scarica',
    seeAllEvolution: 'Vedi evoluzione completa',
    triggers: {
      onboarding: 'Configurazione iniziale',
      entry_analysis: 'Analisi momento',
      manual_edit: 'Modifica manuale',
      auto_evolution: 'Evoluzione automatica',
      milestone: 'Traguardo raggiunto',
      chat_insight: 'Insight dalla conversazione'
    }
  },
  English: {
    title: 'Profile',
    subtitle: 'How Egovex sees you',
    summary: 'Who you are',
    values: 'Core Values',
    goals: 'Life Goals',
    blockers: 'Challenges to Overcome',
    people: 'Important People',
    places: 'Significant Places',
    traits: 'Distinctive Traits',
    evolution: 'Profile Evolution',
    evolutionComment: 'Your profile evolves automatically based on the moments you share. Each reflection enriches the understanding of who you are.',
    version: 'Version',
    completeness: 'Completeness',
    lastUpdated: 'Last updated',
    created: 'Created',
    noProfile: 'Profile under construction...',
    noProfileDesc: 'Complete more moments to help me know you better',
    share: 'Share',
    download: 'Download',
    seeAllEvolution: 'See complete evolution',
    triggers: {
      onboarding: 'Initial setup',
      entry_analysis: 'Moment analysis',
      manual_edit: 'Manual edit',
      auto_evolution: 'Automatic evolution',
      milestone: 'Milestone reached',
      chat_insight: 'Insight from conversation'
    }
  },
  Spanish: {
    title: 'Perfil',
    subtitle: 'Cómo Egovex te ve',
    summary: 'Quién eres',
    values: 'Valores Fundamentales',
    goals: 'Objetivos de Vida',
    blockers: 'Desafíos a Superar',
    people: 'Personas Importantes',
    places: 'Lugares Significativos',
    traits: 'Rasgos Distintivos',
    evolution: 'Evolución del Perfil',
    evolutionComment: 'Tu perfil evoluciona automáticamente según los momentos que compartes. Cada reflexión enriquece la comprensión de quién eres.',
    version: 'Versión',
    completeness: 'Completitud',
    lastUpdated: 'Última actualización',
    created: 'Creado',
    noProfile: 'Perfil en construcción...',
    noProfileDesc: 'Completa más momentos para ayudarme a conocerte mejor',
    share: 'Compartir',
    download: 'Descargar',
    seeAllEvolution: 'Ver evolución completa',
    triggers: {
      onboarding: 'Configuración inicial',
      entry_analysis: 'Análisis del momento',
      manual_edit: 'Edición manual',
      auto_evolution: 'Evolución automática',
      milestone: 'Logro alcanzado',
      chat_insight: 'Insight de la conversación'
    }
  },
  German: {
    title: 'Profil',
    subtitle: 'Wie Egovex dich sieht',
    summary: 'Wer du bist',
    values: 'Kernwerte',
    goals: 'Lebensziele',
    blockers: 'Herausforderungen zu Überwinden',
    people: 'Wichtige Menschen',
    places: 'Bedeutende Orte',
    traits: 'Charakteristische Merkmale',
    evolution: 'Profilentwicklung',
    evolutionComment: 'Dein Profil entwickelt sich automatisch basierend auf den Momenten, die du teilst. Jede Reflexion bereichert das Verständnis, wer du bist.',
    version: 'Version',
    completeness: 'Vollständigkeit',
    lastUpdated: 'Zuletzt aktualisiert',
    created: 'Erstellt',
    noProfile: 'Profil wird erstellt...',
    noProfileDesc: 'Vervollständige mehr Momente, damit ich dich besser kennenlernen kann',
    share: 'Teilen',
    download: 'Herunterladen',
    seeAllEvolution: 'Vollständige Entwicklung ansehen',
    triggers: {
      onboarding: 'Ersteinrichtung',
      entry_analysis: 'Momentanalyse',
      manual_edit: 'Manuelle Bearbeitung',
      auto_evolution: 'Automatische Entwicklung',
      milestone: 'Meilenstein erreicht',
      chat_insight: 'Einsicht aus dem Gespräch'
    }
  },
  French: {
    title: 'Profil',
    subtitle: 'Comment Egovex te voit',
    summary: 'Qui tu es',
    values: 'Valeurs Fondamentales',
    goals: 'Objectifs de Vie',
    blockers: 'Défis à Relever',
    people: 'Personnes Importantes',
    places: 'Lieux Significatifs',
    traits: 'Traits Distinctifs',
    evolution: 'Évolution du Profil',
    evolutionComment: 'Ton profil évolue automatiquement en fonction des moments que tu partages. Chaque réflexion enrichit la compréhension de qui tu es.',
    version: 'Version',
    completeness: 'Complétude',
    lastUpdated: 'Dernière mise à jour',
    created: 'Créé',
    noProfile: 'Profil en construction...',
    noProfileDesc: 'Complète plus de moments pour m\'aider à mieux te connaître',
    share: 'Partager',
    download: 'Télécharger',
    seeAllEvolution: 'Voir l\'évolution complète',
    triggers: {
      onboarding: 'Configuration initiale',
      entry_analysis: 'Analyse du moment',
      manual_edit: 'Modification manuelle',
      auto_evolution: 'Évolution automatique',
      milestone: 'Jalon atteint',
      chat_insight: 'Insight de la conversation'
    }
  }
};


const MyProfileView: React.FC<MyProfileViewProps> = ({ context, onNavigate, onContextUpdate }) => {
  const lang = context.language === 'Italian' ? 'Italian' : context.language === 'Spanish' ? 'Spanish' : context.language === 'German' ? 'German' : context.language === 'French' ? 'French' : 'English';
  const t = STRINGS[lang];

  const profile = context.aiProfile;

  // State for managing rejected blockers (locally filtered)
  const [localRejectedBlockers, setLocalRejectedBlockers] = useState<Set<string>>(new Set());
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Load rejected blockers on mount
  useEffect(() => {
    const loadRejected = async () => {
      try {
        const res = await fetch('/api/insight-feedback/rejected');
        if (res.ok) {
          const data = await res.json();
          setLocalRejectedBlockers(new Set(data.rejectedBlockers || []));
        }
      } catch (e) {
        console.error('Failed to load rejected blockers:', e);
      }
    };
    loadRejected();
  }, []);

  // Handle blocker feedback
  const handleBlockerFeedback = useCallback(async (
    id: string,
    content: string,
    feedbackType: 'helpful' | 'not_helpful'
  ) => {
    setIsSubmittingFeedback(true);
    try {
      await fetch('/api/insight-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insightType: 'blocker',
          insightContent: content,
          feedbackType,
          relatedEntityId: id,
          relatedEntityType: 'blocker',
          contextSnapshot: {
            profileVersion: profile?.version,
            timestamp: Date.now()
          }
        })
      });

      // If rejected, add to local state immediately for instant UI feedback
      if (feedbackType === 'not_helpful') {
        setLocalRejectedBlockers(prev => new Set([...prev, content]));
      }
    } catch (e) {
      console.error('Failed to submit blocker feedback:', e);
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [profile?.version]);

  // Remove blocker from local view
  const handleBlockerDismiss = useCallback((id: string) => {
    // Already handled by the localRejectedBlockers state update
  }, []);

  // Filter out rejected blockers from profile
  const visibleBlockers = useMemo(() => {
    if (!profile?.blockers) return [];
    return profile.blockers.filter(
      blocker => !localRejectedBlockers.has(blocker.blocker)
    );
  }, [profile?.blockers, localRejectedBlockers]);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(lang === 'Italian' ? 'it-IT' : lang === 'Spanish' ? 'es-ES' : lang === 'German' ? 'de-DE' : lang === 'French' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!profile) {
    return (
      <div className="h-full flex flex-col bg-[#F5F5F7] view-enter overflow-hidden">
        <header className="px-6 lg:px-12 xl:px-16 2xl:px-20 pt-6 lg:pt-6 pb-4 lg:pb-5 shrink-0 bg-white/95 backdrop-blur-xl border-b border-black/[0.03]">
          <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
            <h1 className="text-[32px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none text-[#1D1D1F]">{t.title}.</h1>
          </div>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <User size={32} className="text-gray-400" />
            </div>
            <h2 className="text-2xl font-black text-black mb-3">{t.noProfile}</h2>
            <p className="text-gray-600 text-base mb-8">{t.noProfileDesc}</p>
            <button
              onClick={() => onNavigate('pulse')}
              className="px-6 py-3 bg-black text-white font-black rounded-2xl hover:bg-gray-800 transition-all active:scale-95"
            >
              {lang === 'Italian' ? 'Vai alla Home' : lang === 'Spanish' ? 'Ir al Inicio' : lang === 'German' ? 'Zur Startseite' : lang === 'French' ? 'Aller à l\'accueil' : 'Go to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F5F5F7] view-enter overflow-hidden">
      <header className="px-6 lg:px-12 xl:px-16 2xl:px-20 pt-6 lg:pt-6 pb-4 lg:pb-5 shrink-0 bg-white/95 backdrop-blur-xl border-b border-black/[0.03]">
        <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-[32px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none text-[#1D1D1F] mb-1">{t.title}.</h1>
              <p className="text-[13px] lg:text-sm text-black/40 font-medium">{t.subtitle}</p>
            </div>
            <div className="flex gap-2">
              <button className="w-10 h-10 bg-black/5 hover:bg-black/10 rounded-2xl flex items-center justify-center transition-all text-black/40 hover:text-black/60">
                <Share2 size={18} />
              </button>
              <button className="w-10 h-10 bg-black/5 hover:bg-black/10 rounded-2xl flex items-center justify-center transition-all text-black/40 hover:text-black/60">
                <Download size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 lg:px-12 xl:px-20 2xl:px-32 py-6 lg:py-8 no-scrollbar pb-32 lg:pb-44">
        <div className="max-w-6xl 2xl:max-w-7xl mx-auto space-y-5 lg:space-y-6">
          
          <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm flex items-center gap-3">
            <Info size={18} className="text-indigo-400 shrink-0" />
            <p className="text-[13px] lg:text-sm text-black/60 font-medium">
              {t.evolutionComment}
            </p>
          </div>

          {profile.summary && (
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <Heart size={16} className="text-pink-500" />
                <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.summary}</span>
              </div>
              <p className="text-[15px] lg:text-base text-black/80 leading-relaxed font-medium">
                {profile.summary}
              </p>
            </div>
          )}

          {profile.coreValues && profile.coreValues.length > 0 && (
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-purple-500" />
                <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.values}</span>
              </div>
              <div className="space-y-4">
                {profile.coreValues.map((value, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[14px] font-black text-black">{value.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-bold text-purple-600">{value.strength}%</span>
                        {value.trend && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            value.trend === 'growing' ? 'bg-green-100 text-green-700' :
                            value.trend === 'declining' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {value.trend === 'growing' ? (lang === 'Italian' ? 'in crescita' : lang === 'Spanish' ? 'creciendo' : lang === 'German' ? 'wachsend' : lang === 'French' ? 'en croissance' : 'growing') :
                             value.trend === 'declining' ? (lang === 'Italian' ? 'in calo' : lang === 'Spanish' ? 'decayendo' : lang === 'German' ? 'abnehmend' : lang === 'French' ? 'en déclin' : 'declining') :
                             (lang === 'Italian' ? 'stabile' : lang === 'Spanish' ? 'estable' : lang === 'German' ? 'stabil' : lang === 'French' ? 'stable' : 'stable')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000"
                        style={{ width: `${value.strength}%` }}
                      />
                    </div>
                    {value.examples && value.examples.length > 0 && (
                      <p className="text-[12px] text-black/50 mt-2 italic">"{value.examples[0]}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile.lifeGoals && profile.lifeGoals.length > 0 && (
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-blue-500" />
                <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.goals}</span>
              </div>
              <div className="space-y-3">
                {profile.lifeGoals.map((goal, i) => (
                  <div key={i} className="p-4 bg-[#F5F5F7] rounded-2xl">
                    <h3 className="text-[14px] font-bold text-black mb-1">{goal.goal}</h3>
                    {goal.why && <p className="text-[12px] text-black/50 mb-3">{goal.why}</p>}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-1000"
                          style={{ width: `${goal.progress}%` }}
                        />
                      </div>
                      <span className="text-[11px] font-bold text-black/40">{goal.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {visibleBlockers.length > 0 && (
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.blockers}</span>
                </div>
                <span className="text-[10px] text-black/30 font-medium">
                  {lang === 'Italian' ? 'Scorri per rimuovere' :
                   lang === 'Spanish' ? 'Desliza para eliminar' :
                   lang === 'German' ? 'Wischen zum Entfernen' :
                   lang === 'French' ? 'Glissez pour supprimer' :
                   'Swipe to remove'}
                </span>
              </div>
              <div className="space-y-2">
                {visibleBlockers.map((blocker, i) => (
                  <SwipeableFeedbackCard
                    key={`blocker-${i}-${blocker.blocker}`}
                    id={`blocker-${i}`}
                    content={blocker.blocker}
                    subContent={blocker.context}
                    type="blocker"
                    onFeedback={handleBlockerFeedback}
                    onDismiss={handleBlockerDismiss}
                    language={lang}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
            {profile.keyPeople && profile.keyPeople.length > 0 && (
              <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={16} className="text-emerald-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.people}</span>
                </div>
                <div className="space-y-2">
                  {profile.keyPeople.map((person, i) => (
                    <div key={i} className="p-3 bg-[#F5F5F7] rounded-xl flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-[12px] font-black text-emerald-600">
                          {person.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[13px] font-bold text-black block truncate">{person.name}</span>
                        {person.relationship && (
                          <span className="text-[11px] text-black/40">{person.relationship}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profile.keyPlaces && profile.keyPlaces.length > 0 && (
              <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={16} className="text-rose-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.places}</span>
                </div>
                <div className="space-y-2">
                  {profile.keyPlaces.map((place, i) => (
                    <div key={i} className="p-3 bg-[#F5F5F7] rounded-xl">
                      <span className="text-[13px] font-bold text-black block mb-1">{place.name}</span>
                      {place.significance && (
                        <p className="text-[11px] text-black/50">{place.significance}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {profile.personalityTraits && profile.personalityTraits.length > 0 && (
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <User size={16} className="text-indigo-500" />
                <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.traits}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.personalityTraits.map((trait, i) => (
                  <div 
                    key={i} 
                    className="px-3 py-1.5 bg-indigo-50 rounded-full"
                    title={trait.evidence?.join(', ')}
                  >
                    <span className="text-[12px] font-bold text-indigo-700">{trait.trait}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile.updateHistory && profile.updateHistory.length > 0 && (
            <div className="bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-500" />
                  <span className="text-[11px] font-bold text-black/40 uppercase tracking-wider">{t.evolution}</span>
                </div>
                <span className="text-[11px] font-bold text-black/30">v{profile.version}</span>
              </div>

              <div className="space-y-3">
                {profile.updateHistory.slice(-3).reverse().map((update, i) => (
                  <div key={i} className="p-3 bg-[#F5F5F7] rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold text-black/60">
                        {t.triggers[update.trigger as keyof typeof t.triggers] || update.trigger}
                      </span>
                      <span className="text-[10px] text-black/30 font-medium">
                        {formatDate(update.timestamp)}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {update.changes.map((change, j) => (
                        <li key={j} className="text-[12px] text-black/50 flex items-start gap-2">
                          <span className="text-black/30 shrink-0 mt-1">•</span>
                          <span>{change}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {profile.profileHistory && profile.profileHistory.length > 0 && (
                <button
                  onClick={() => onNavigate('evolution')}
                  className="w-full mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
                >
                  <span className="text-[13px] font-bold">{t.seeAllEvolution}</span>
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyProfileView;
